import React, { useEffect, useRef, useState } from 'react';
import Radar from 'radar-sdk-js';
import { useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';

import { guestLogin } from 'src/Features/AccountSetup/redux/actions';
import { setLocation } from 'src/Features/Location/redux/slice';
import {
  DELIVERY_DESTINATION_ID,
  DELIVERY_ORDER_TYPE,
  FULL_SIZE,
  IN_STORE_DESTINATION_ID,
  PICK_UP_METHOD_IN_STORE,
  PICK_UP_ORDER_TYPE,
  QUERY_KEY_BERRY_SESSION_DETAIL,
} from 'src/constants';
import { cartSubTotal } from 'src/helper/cartHelperMethods';
import { getUser, getVisitorId, isGuestUser } from 'src/helper/helperMethods';
import {
  IBerryCartLine,
  IBerryChatResponse,
  IBerryRecommendedItem,
} from 'src/models/berry.model';
import { IAddToOrderPayload } from 'src/models/order.model';
import {
  addItemToOrder,
  setShowCart,
  updateIsOrderTypeSelected,
  updateOrderDetails,
  updateCartOrder,
  updateOrderType,
} from 'src/redux/slices/cartSlice';
import { useAppDispatch, useAppSelector } from 'src/redux/store/store';
import { locationMenuService } from 'src/services';
import { berryApiService } from 'src/services/berry.service';
import { getCart } from 'src/react-query-hooks/Cart/useGetCartItems';
import useBerryAddToCart from './hooks/useBerryAddToCart';
import useBerryChat from './hooks/useBerryChat';
import useBerryGetSession from './hooks/useBerryGetSession';
import useBerrySession from './hooks/useBerrySession';
import { IChatMessage } from './types';
import {
  IBerryItemSelection,
  buildCartItemFromSelection,
  selectionLineTotal,
} from './helper/berryItemConfig.helper';
import {
  clearBerryChat,
  loadBerryChat,
  saveBerryChat,
  toSlotTime,
} from './helper/berryChatStorage';
import ChatPanel from './components/ChatPanel';
import DeliveryAddressConfirmModal, {
  IBerryDeliveryAddress,
} from './components/DeliveryAddressConfirmModal';
import ItemConfigPicker from './components/ItemConfigPicker';
import styles from './berryChat.module.scss';

const BerryChatWidget: React.FC = () => {
  const dispatch = useAppDispatch();

  // A refresh restores the chat from this tab's sessionStorage (B85).
  const restored = useRef(loadBerryChat()).current;
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(restored?.sessionId ?? null);
  const [messages, setMessages] = useState<IChatMessage[]>(restored?.messages ?? []);
  const [berryCartId, setBerryCartId] = useState<number | null>(restored?.berryCartId ?? null);
  const [runningTotal, setRunningTotal] = useState(0);
  // The real cart as Pulse prices it (the site's own drawer math —
  // cartSubTotal over Pulse's gross_price). Follows edits made in the cart
  // drawer too (B97), since the drawer updates the same store.
  const siteCartItems = useAppSelector((state: any) => state.cart?.items ?? []);
  const siteOrderDetails = useAppSelector((state: any) => state.cart?.orderDetails ?? null);
  const realCartTotal: number | null =
    berryCartId != null && Array.isArray(siteCartItems) && siteCartItems.length > 0
      ? cartSubTotal(siteCartItems)
      : null;

  // Tray state — widget-level so added state survives across tray changes
  const [trayItems, setTrayItems] = useState<IBerryRecommendedItem[] | null>(
    restored?.trayItems ?? null,
  );
  const [trayDismissed, setTrayDismissed] = useState(restored?.trayDismissed ?? false);
  const [addedItemIds, setAddedItemIds] = useState<Set<number>>(
    new Set(restored?.addedItemIds ?? []),
  );
  const [checkoutLocation, setCheckoutLocation] = useState<string | null>(
    restored?.checkoutLocation ?? null,
  );
  // Every card Berry has shown this chat, newest set last — so earlier
  // suggestions stay reachable when a new reply replaces the tray (B76).
  const [seenItems, setSeenItems] = useState<IBerryRecommendedItem[]>(restored?.seenItems ?? []);
  // Puts the customer's text back in the input after a failed send (B83).
  const [inputRestore, setInputRestore] = useState<{ text: string; nonce: number } | null>(null);

  // Full Radar location object — stored so the confirm handler can re-dispatch setLocation.
  const [fullLocationObject, setFullLocationObject] = useState<any>(null);

  // Delivery address confirmation popup
  const [showDeliveryConfirm, setShowDeliveryConfirm] = useState(false);
  const [parsedDeliveryAddress, setParsedDeliveryAddress] = useState<IBerryDeliveryAddress | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Item configuration picker (sandwich/soup tray, package) — set when a
  // configurable recommended-item card is tapped; cleared on cancel/confirm.
  const [configTarget, setConfigTarget] = useState<IBerryRecommendedItem | null>(null);
  // Which entry of configTarget.size_plan the picker is on — "1 Large + 1
  // Small" opens the Large, then the Small (UAT row 12).
  const [configPlanStep, setConfigPlanStep] = useState(0);

  const sessionStarted = useRef(Boolean(restored?.sessionId));
  const guestAuthDone = useRef(false);
  // Everything this widget has added to the Pulse cart this session. Sent to
  // Berry with each cart_updated message — Berry can't read the cart, and
  // without this it told a customer items "were never added" (E2E B79).
  const cartLines = useRef<IBerryCartLine[]>(restored?.cartLines ?? []);
  // Last cart total Berry was told about, so a drawer edit is reported once.
  const lastReportedTotal = useRef<number | null>(null);
  // `${location_id}|${order_type}` last pushed to the site's Redux store.
  const appliedSyncKey = useRef<string | null>(null);
  // State (not ref) so sessionReady re-derives when it flips, gating the Add button.
  const [locationSetDone, setLocationSetDone] = useState(false);

  const queryClient = useQueryClient();

  const { mutate: startSession, isLoading: sessionLoading } = useBerrySession();
  const { mutate: sendMessage, isLoading: chatLoading } = useBerryChat();
  const { mutateAsync: addToCartAsync } = useBerryAddToCart();
  const { data: sessionDetail, error: sessionError } = useBerryGetSession(sessionId);

  // locationSetDone is required so the Add button is not enabled until the full location
  // object has been resolved, setLocation has dispatched, and the resulting cart-sync has
  // settled (see effect below).
  const sessionReady = Boolean(
    sessionDetail?.location_id && sessionDetail?.event_date && locationSetDone,
  );

  // Syncs tray and checkout location from any Berry response
  const syncTrayAndCheckout = (data: IBerryChatResponse) => {
    if (data.recommended_items && data.recommended_items.length > 0) {
      setTrayItems(data.recommended_items);
      setTrayDismissed(false);
      setSeenItems((prev) => {
        const fresh = new Set(data.recommended_items.map((i) => i.item_id));
        return [...prev.filter((i) => !fresh.has(i.item_id)), ...data.recommended_items];
      });
    }
    if (data.cart_summary?.location) {
      setCheckoutLocation(data.cart_summary.location);
    }
  };

  // Persist the chat for this tab after every change (B85).
  useEffect(() => {
    if (!sessionId) return;
    saveBerryChat({
      sessionId,
      messages,
      berryCartId,
      addedItemIds: [...addedItemIds],
      trayItems,
      trayDismissed,
      seenItems,
      cartLines: cartLines.current,
      checkoutLocation,
    });
  }, [sessionId, messages, berryCartId, addedItemIds, trayItems, trayDismissed, seenItems, checkoutLocation]);

  // Tell Berry when the cart changes outside the chat (quantity edits or
  // removals in the cart drawer), silently — no reply, no bubble — so Berry's
  // own cart total and lines stay true (B97/R9). Debounced; skipped when the
  // total is what Berry was last told.
  useEffect(() => {
    if (!sessionId || berryCartId == null || realCartTotal == null) return undefined;
    if (
      lastReportedTotal.current != null &&
      Math.abs(realCartTotal - lastReportedTotal.current) < 0.005
    ) {
      return undefined;
    }
    const handle = setTimeout(() => {
      const lines: IBerryCartLine[] = (siteCartItems as any[]).map((i) => ({
        name: String(i?.name ?? 'Item'),
        quantity: Number(i?.quantity ?? 1),
      }));
      cartLines.current = lines;
      lastReportedTotal.current = realCartTotal;
      berryApiService
        .sendMessage({
          session_id: sessionId,
          message: `__system__:cart_synced:cart_id=${berryCartId}:total=${realCartTotal.toFixed(2)}`,
          cart_items: lines,
        })
        .catch(() => {
          lastReportedTotal.current = null; // retry on the next change
        });
    }, 800);
    return () => clearTimeout(handle);
  }, [realCartTotal, berryCartId, sessionId]);

  // A restored session Redis no longer has (expired after 4h) → start over
  // instead of chatting into a 404.
  useEffect(() => {
    const status = (sessionError as any)?.response?.status;
    if (restored?.sessionId && sessionId === restored.sessionId && status === 404) {
      clearBerryChat();
      sessionStarted.current = false;
      setSessionId(null);
      setMessages([]);
      setBerryCartId(null);
      setTrayItems(null);
      setSeenItems([]);
      setAddedItemIds(new Set());
      cartLines.current = [];
      if (isOpen) {
        sessionStarted.current = true;
        startSession(undefined, {
          onSuccess: (data) => {
            setSessionId(data.session_id);
            setMessages([{ id: uuidv4(), role: 'assistant', text: data.greeting_message }]);
          },
        });
      }
    }
  }, [sessionError]);

  useEffect(() => {
    if (isOpen && !sessionStarted.current) {
      sessionStarted.current = true;
      startSession(undefined, {
        onSuccess: (data) => {
          setSessionId(data.session_id);
          setMessages([
            {
              id: uuidv4(),
              role: 'assistant',
              text: data.greeting_message,
            },
          ]);
        },
        onError: () => {
          setMessages([
            {
              id: uuidv4(),
              role: 'assistant',
              text: 'Something went wrong starting your session. Please close and try again.',
              isError: true,
            },
          ]);
        },
      });
    }
  }, [isOpen]);

  // Sets the site's selected location and order type from Berry's session, then waits
  // for Cart.tsx's resulting sync to complete before unblocking the Add button.
  //
  // WHY this ordering matters (Step 1 finding 3):
  // dispatch(setLocation) → Cart.tsx useEffect([location_id]) → syncCart() → getCart()
  // from Pulse. If the Add button were enabled immediately after dispatch, a logged-in
  // user could write to Pulse while that getCart is still in flight. When the response
  // arrives, Cart.tsx dispatches updateCartOrder/clearCart and silently overwrites the
  // just-added item in Redux.
  //
  // FIX: after dispatch(setLocation), we await our own getCart() for the same location.
  // Cart.tsx's concurrent syncCart() hits the same endpoint at the same time. When our
  // await resolves, Cart.tsx's response has also landed — we then flip locationSetDone,
  // which is included in sessionReady, which gates the Add button.
  //
  // Guest users: Cart.tsx's syncCart bails immediately (no user.id), so the getCart
  // race cannot occur — we skip the extra call and flip locationSetDone right away.
  //
  // Re-runs whenever Berry's store OR order type changes, not just the first time.
  // It used to stop after the first sync (guarded on locationSetDone), so a switch
  // pickup ↔ delivery or to another store mid-conversation never reached the site:
  // a customer who switched to pickup to avoid the fee was still charged delivery,
  // and a store switch left the cart on the old store (E2E 2026-09-29, B61/B108).
  useEffect(() => {
    const locationId = sessionDetail?.location_id;
    if (!locationId) return;
    const syncKey = `${locationId}|${sessionDetail?.order_type ?? ''}`;
    if (appliedSyncKey.current === syncKey) return;
    appliedSyncKey.current = syncKey;
    // Re-gate the Add button until the new store/order type has settled.
    setLocationSetDone(false);

    const resolveAndSetLocation = async () => {
      try {
        const response = await locationMenuService.getLocationDetails(locationId);
        const fullLocation = response.data.data;
        setFullLocationObject(fullLocation);

        // Same call as LocationInfo.handleSelectLocation — writes Redux selectedStore.
        // Does NOT call updateOrderDetails(null) or force PICK_UP (unlike LocationInfo).
        dispatch(setLocation(fullLocation));

        // Preserve the Berry session's order type instead of letting the site default
        // to PICK_UP. Berry returns "delivery" or "pickup" (lowercase). Cart.tsx
        // re-syncs on orderType, and Billing adds the delivery fee only for DELIVERY.
        if (sessionDetail?.order_type) {
          dispatch(
            updateOrderType(
              sessionDetail?.order_type === 'delivery'
                ? DELIVERY_ORDER_TYPE
                : PICK_UP_ORDER_TYPE,
            ),
          );
        }

        // Required for the header's locationLabel() to show the selected store name.
        // The header gates on cart.isOrderTypeSelected before reading selectedStore;
        // without this dispatch it always falls through to "find a cafe zupas".
        // Safe: does not wipe delivery details (updateOrderDetails) or force pickup.
        dispatch(updateIsOrderTypeSelected(true));

        // For logged-in users: await a getCart round-trip to guarantee Cart.tsx's
        // concurrent syncCart has completed before we flip locationSetDone.
        // Errors here are non-fatal (empty cart returns an error on some backends);
        // the important thing is the round-trip has finished.
        //
        // Require BOTH id AND token: a stale customer_id in storage with no active
        // token means the session is a guest — sending getCart with that id causes
        // a 401. Guests skip this probe entirely (Cart.tsx's syncCart bails for them
        // anyway), so locationSetDone flips immediately after setLocation.
        const currentUser = getUser();
        if (currentUser?.id && currentUser?.token) {
          try {
            await getCart({
              location_id: String(fullLocation.id),
              customer_id: String(currentUser.id),
            });
          } catch {
            // Empty cart or network error — Cart.tsx's sync has still run; proceed.
          }
        }

        setLocationSetDone(true);
      } catch (err) {
        console.error('[Berry] Failed to resolve full location:', err);
        // locationSetDone stays false; Add button stays disabled. Berry cannot write
        // to cart without a confirmed location. Clearing the key lets the next
        // session refetch retry.
        appliedSyncKey.current = null;
      }
    };

    resolveAndSetLocation();
  }, [sessionDetail?.location_id, sessionDetail?.order_type]);

  // Called by CartCompleteCard's "Proceed to Checkout" button.
  // For delivery orders: forward-geocodes the raw address, shows confirmation popup.
  // For pickup or when address is unavailable: opens cart immediately.
  const handleCheckout = async () => {
    const isDelivery = sessionDetail?.order_type === 'delivery';
    const rawAddress = sessionDetail?.delivery_address;

    if (sessionDetail?.event_date) {
      dispatch(
        updateOrderDetails({
          ...(siteOrderDetails ?? {}),
          PickupSlotDetails: scheduleFromBerry(),
        }),
      );
    }

    // DIAG: gate conditions
    console.log('[Berry:handleCheckout] sessionDetail:', sessionDetail);
    console.log('[Berry:handleCheckout] order_type =', sessionDetail?.order_type, '| isDelivery =', isDelivery);
    console.log('[Berry:handleCheckout] delivery_address =', rawAddress, '| truthy =', Boolean(rawAddress));
    console.log('[Berry:handleCheckout] gate passes (isDelivery && rawAddress) =', Boolean(isDelivery && rawAddress));

    if (isDelivery && rawAddress) {
      console.log('[Berry:handleCheckout] BRANCH: geocode-and-popup — calling Radar.forwardGeocode');
      setIsGeocoding(true);
      try {
        const result = await Radar.forwardGeocode({ query: rawAddress });
        console.log('[Berry:handleCheckout] Radar.forwardGeocode SUCCESS — raw result:', result);
        const addr = result.addresses?.[0];
        if (!addr) {
          console.log('[Berry:handleCheckout] Radar returned 0 addresses — falling back to open cart directly');
          dispatch(setShowCart(true));
          return;
        }
        console.log('[Berry:handleCheckout] Using first address:', addr);
        const streetName = [addr.number, addr.street].filter(Boolean).join(' ');
        const currentUser = getUser();
        const mapped: IBerryDeliveryAddress = {
          address_name: streetName,
          street: streetName,
          street_name: streetName,
          city: addr.city ?? '',
          state: addr.stateCode ?? '',
          zipcode: addr.postalCode ?? '',
          country: addr.countryCode ?? 'US',
          apartment_no: '',
          full_address: addr.formattedAddress ?? rawAddress,
          isGuestAddress: isGuestUser(currentUser),
          id: null,
        };
        console.log('[Berry:handleCheckout] mapped delivery_address:', mapped);
        setParsedDeliveryAddress(mapped);
        setShowDeliveryConfirm(true);
      } catch (err) {
        console.error('[Berry:handleCheckout] Radar.forwardGeocode THREW — falling back to open cart. Error:', err);
        dispatch(setShowCart(true));
      } finally {
        setIsGeocoding(false);
      }
    } else {
      console.log('[Berry:handleCheckout] BRANCH: fallback-open-cart — reason: isDelivery =', isDelivery, ', rawAddress =', rawAddress);
      dispatch(setShowCart(true));
    }
  };

  // Dispatches the same handoff sequence as ItemDelivery.handleLocation, then opens cart.
  // The date and time Berry collected, in the site's own checkout shape
  // (PickupSlots' PickupSlotDetails), so checkout doesn't ask again (B52).
  // The site re-checks the time against the store's available slots and asks
  // for another if it's gone, so a stale time can't be ordered.
  const scheduleFromBerry = () => {
    const date = sessionDetail?.event_date;
    if (!date) return siteOrderDetails?.PickupSlotDetails;
    const isDelivery = sessionDetail?.order_type === 'delivery';
    return {
      ...(siteOrderDetails?.PickupSlotDetails ?? {}),
      date,
      time: toSlotTime(sessionDetail?.event_time) ?? '',
      pickupMethod: isDelivery
        ? DELIVERY_ORDER_TYPE
        : siteOrderDetails?.PickupSlotDetails?.pickupMethod ?? PICK_UP_METHOD_IN_STORE,
      isAsapOrder: false,
      asapMinutes: null,
    };
  };

  const handleDeliveryConfirm = () => {
    if (!parsedDeliveryAddress || !fullLocationObject) return;

    dispatch(
      updateOrderDetails({
        ...(siteOrderDetails ?? {}),
        delivery_address: parsedDeliveryAddress,
        PickupSlotDetails: scheduleFromBerry(),
      }),
    );
    dispatch(updateIsOrderTypeSelected(true));
    dispatch(setLocation(fullLocationObject));
    queryClient.refetchQueries(['get-cart', fullLocationObject.id]);
    dispatch(updateOrderType(DELIVERY_ORDER_TYPE));
    setShowDeliveryConfirm(false);
    dispatch(setShowCart(true));
  };

  const handleSend = (text: string) => {
    if (!sessionId || chatLoading) return;

    setMessages((prev) => [
      ...prev,
      { id: uuidv4(), role: 'user', text },
    ]);

    sendMessage(
      { session_id: sessionId, message: text },
      {
        onSuccess: (data) => {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              role: 'assistant',
              text: data.response,
              recommendedItems: data.recommended_items ?? [],
              cartSummary: data.cart_summary ?? null,
              conversationStage: data.conversation_stage,
            },
          ]);
          syncTrayAndCheckout(data);
          // Session state (location_id, event_date) is updated server-side as
          // the conversation advances — refetch so sessionReady reflects it.
          queryClient.invalidateQueries([QUERY_KEY_BERRY_SESSION_DETAIL, sessionId]);
        },
        onError: (error: any) => {
          // Say why when the backend says why ("Message too long…"), and give
          // the customer their text back instead of losing it (B83).
          const detail = error?.response?.status === 400 ? error?.response?.data?.detail : null;
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              role: 'assistant',
              text:
                typeof detail === 'string' && detail
                  ? detail
                  : 'Something went wrong. Please try again.',
              isError: true,
            },
          ]);
          setInputRestore({ text, nonce: Date.now() });
        },
      },
    );
  };

  // Shared cart-write core — used by both the direct "Add to Cart" path
  // (simple recommended items) and the item-config picker's "Add to Cart"
  // (sandwich/soup tray, package). Guest auth guard, Pulse write, Redux sync,
  // and Berry notification all happen exactly once here regardless of which
  // path built `cartItem`. `lineTotal` is the caller's unit price already
  // multiplied by whatever quantity the customer chose — see
  // handleAddToCart/handleConfigConfirm — so the running-total strip (and the
  // eventual real cart) reflect quantity, not just a single unit.
  const commitCartItem = async (
    cartItem: NonNullable<IAddToOrderPayload['items']>[number],
    lineTotal: number,
  ): Promise<void> => {
    if (!sessionDetail?.location_id || !sessionDetail?.event_date) {
      throw new Error('Berry needs your location and event date first.');
    }

    // Guest auth guard — run at most once across all adds
    if (!guestAuthDone.current) {
      const currentUser = getUser();
      if (!currentUser.token) {
        const ok = await guestLogin(dispatch, { visitor_id: getVisitorId() });
        if (!ok) {
          throw new Error('Unable to start your session. Please try again.');
        }
      }
      guestAuthDone.current = true;
    }

    const authInfo = getUser();
    // Local estimate (picker deltas included, rounded to cents — B13). Only
    // used if the cart can't be read back from Pulse below.
    const newTotal = Math.round((runningTotal + lineTotal) * 100) / 100;

    // `is_catering` is real on the wire (the site sends it) but not on the
    // shared IAddToOrderPayload type, which this widget does not edit.
    const cartPayload: IAddToOrderPayload & { is_catering: boolean } = {
      source: 'web_app',
      status: 'open',
      business_date: sessionDetail.event_date,
      total_price: newTotal,
      sub_total: newTotal,
      location_id: sessionDetail.location_id,
      // The site's own item page sends this on every catering cart write
      // (captured 2026-10-06); Berry's writes now match.
      is_catering: true,
      is_agent_created_cart: true,
      ...(authInfo.id && authInfo.token
        ? { customer_id: Number(authInfo.id) }
        : { visitor_id: authInfo.visitor_id ?? getVisitorId() }),
      items: [cartItem] as IAddToOrderPayload['items'],
    };

    const cartData = await addToCartAsync(cartPayload);
    const newCartId = cartData.id;

    setBerryCartId(newCartId);
    cartLines.current = [
      ...cartLines.current,
      { name: String(cartItem.name ?? 'Item'), quantity: Number(cartItem.quantity ?? 1) },
    ];

    // Read the cart back from Pulse — the same call the site's drawer makes
    // (useSyncCart) — so the banner, Berry's cart_summary and the sentinel
    // carry what the customer will actually pay, paid choices included
    // (B2/B14/B50: the widget used to send its own base-price sum). Also
    // fills the drawer straight away instead of an empty first open (B68).
    let confirmedTotal = newTotal;
    try {
      const cart = await getCart({
        location_id: String(sessionDetail.location_id),
        ...(authInfo.id && authInfo.token
          ? { customer_id: String(authInfo.id) }
          : { visitor_id: authInfo.visitor_id ?? getVisitorId() }),
        order_type:
          sessionDetail.order_type === 'delivery' ? DELIVERY_DESTINATION_ID : IN_STORE_DESTINATION_ID,
      });
      if (cart && Array.isArray(cart.items) && cart.items.length > 0) {
        dispatch(updateCartOrder(cart));
        confirmedTotal = cartSubTotal(cart.items);
        // Everything really in the cart (B97/R9, B110), with the line just
        // added last — Berry names the last line in its reply.
        const lines: IBerryCartLine[] = cart.items.map((i: any) => ({
          name: String(i.name ?? 'Item'),
          quantity: Number(i.quantity ?? 1),
        }));
        const addedIdx = lines.map((l) => l.name).lastIndexOf(String(cartItem.name ?? ''));
        if (addedIdx >= 0) lines.push(...lines.splice(addedIdx, 1));
        cartLines.current = lines;
      } else {
        dispatch(addItemToOrder(cartData.items));
      }
    } catch {
      // Mirror the existing useAddToOrder pattern as a fallback: dispatch
      // Pulse's returned items so the badge + drawer still update.
      dispatch(addItemToOrder(cartData.items));
    }
    setRunningTotal(confirmedTotal);
    lastReportedTotal.current = confirmedTotal;

    // Notify Berry about the cart write (fire-and-forget — cart write already succeeded)
    if (sessionId) {
      sendMessage(
        {
          session_id: sessionId,
          message: `__system__:cart_updated:cart_id=${newCartId}:total=${confirmedTotal.toFixed(2)}`,
          cart_items: cartLines.current,
        },
        {
          onSuccess: (data) => {
            setMessages((prev) => [
              ...prev,
              {
                id: uuidv4(),
                role: 'assistant',
                text: data.response,
                recommendedItems: data.recommended_items ?? [],
                cartSummary: data.cart_summary ?? null,
                conversationStage: data.conversation_stage,
              },
            ]);
            syncTrayAndCheckout(data);
          },
        },
      );
    }
  };

  const handleAddToCart = async (item: IBerryRecommendedItem, quantity: number): Promise<void> => {
    await commitCartItem(
      {
        brink_id: 0,
        category_id: item.category_id ?? '',
        // Unit price — Pulse's own `quantity` field carries the multiplier
        // (matches how sub-item picks already report a per-unit display_price
        // alongside a separate quantity), so this stays item.display_price
        // regardless of how many the customer chose.
        display_price: item.display_price,
        id: item.item_id,
        index: 0,
        item_id: item.item_id,
        // NOT item.item_size — that field is display-only copy from Berry's
        // recommendation (used verbatim as card meta text in
        // RecommendedItems.tsx, e.g. "Half tray"), not a Pulse size token.
        // Confirmed against a real production add of the same kind of simple
        // (non-tray) item: item_size/size are always "FULL" — sending the
        // display string here caused Pulse to price the line at $0.
        item_size: FULL_SIZE.id,
        modifiers: [],
        name: item.name,
        price: item.display_price,
        quantity,
        set_id: 1,
        size: FULL_SIZE.id,
      } as NonNullable<IAddToOrderPayload['items']>[number],
      item.display_price * quantity,
    );
  };

  // Opens the compact in-panel picker for a configurable card (package, box
  // lunch, sandwich/soup tray) instead of adding it straight to cart.
  const handleConfigureItem = (item: IBerryRecommendedItem) => {
    setConfigPlanStep(0);
    setConfigTarget(item);
  };

  const handleConfigCancel = () => {
    setConfigTarget(null);
    setConfigPlanStep(0);
  };

  const handleConfigConfirm = async (selection: IBerryItemSelection): Promise<void> => {
    const target = configTarget;
    // Salad trays arrive with the line the site's own engine built.
    const cartItem = (selection.siteCartItem ??
      buildCartItemFromSelection(selection)) as ReturnType<typeof buildCartItemFromSelection>;
    await commitCartItem(cartItem, selectionLineTotal(selection));
    // Berry's plan has another size for this tray ("1 Large + 1 Small"): open
    // it next — unless the customer picked a different size themselves.
    const plan = target?.size_plan ?? [];
    const next = plan[configPlanStep + 1];
    if (target && next && selection.size === plan[configPlanStep]?.size) {
      setConfigPlanStep(configPlanStep + 1);
      setConfigTarget({ ...target, suggested_size: next.size, suggested_quantity: next.quantity });
      return;
    }
    if (target) {
      setAddedItemIds((prev) => new Set([...prev, target.item_id]));
    }
    setConfigTarget(null);
    setConfigPlanStep(0);
  };

  // Wraps handleAddToCart to mark the item added in widget-level state on success
  const handleTrayAdd = async (item: IBerryRecommendedItem, quantity: number): Promise<void> => {
    await handleAddToCart(item, quantity);
    setAddedItemIds((prev) => new Set([...prev, item.item_id]));
  };

  // "Done adding" now says what happens next instead of silently collapsing
  // the cards (B56). No Berry call — it's a local, deterministic nudge.
  //
  // While Berry's last message is still a question ("Would you like to add
  // some drinks?"), it doesn't jump to checkout: the tester saw "Sounds good!
  // … tap Proceed to Checkout below" before Berry answered his drinks question
  // (UAT 2026-10-08, sheet row 14).
  const handleTrayDismiss = () => {
    setTrayDismissed(true);
    setMessages((prev) => {
      const lastBerry = [...prev].reverse().find((m) => m.role === 'assistant');
      const questionOpen = Boolean(lastBerry && /\?\s*\S{0,3}\s*$/.test(lastBerry.text ?? ''));
      let text: string;
      if (questionOpen) {
        text = "Got it — I've put those suggestions away. Tap Show suggestions to see them again.";
      } else if (berryCartId) {
        text = "Sounds good! When you're ready, tap Proceed to Checkout below — or tell me if you'd like anything else.";
      } else {
        text = "No problem! Tell me what else you're looking for, or tap Show suggestions to see those items again.";
      }
      return [...prev, { id: uuidv4(), role: 'assistant', text }];
    });
  };

  // Tray is visible only while there is at least one unadded item in the current set
  const trayVisible =
    trayItems != null &&
    !trayDismissed &&
    trayItems.some((item) => !addedItemIds.has(item.item_id));

  return (
    <>
      {isOpen && (
        <ChatPanel
          messages={messages}
          isLoading={chatLoading}
          isSessionLoading={sessionLoading}
          sessionReady={sessionReady}
          onSend={handleSend}
          onClose={() => setIsOpen(false)}
          trayItems={trayVisible ? trayItems : null}
          earlierItems={
            trayItems
              ? seenItems.filter((s) => !trayItems.some((t) => t.item_id === s.item_id))
              : []
          }
          onTrayReopen={
            trayItems != null && trayDismissed ? () => setTrayDismissed(false) : undefined
          }
          inputRestore={inputRestore}
          dimmed={Boolean(configTarget) || showDeliveryConfirm}
          addedItemIds={addedItemIds}
          onTrayAdd={handleTrayAdd}
          onConfigureItem={handleConfigureItem}
          onTrayDismiss={handleTrayDismiss}
          runningTotal={realCartTotal ?? runningTotal}
          checkoutLocation={checkoutLocation}
          onCheckout={handleCheckout}
          checkoutLoading={isGeocoding}
        />
      )}
      {showDeliveryConfirm && parsedDeliveryAddress && (
        <DeliveryAddressConfirmModal
          show={showDeliveryConfirm}
          address={parsedDeliveryAddress}
          onConfirm={handleDeliveryConfirm}
          onCancel={() => setShowDeliveryConfirm(false)}
        />
      )}
      {configTarget && (
        <ItemConfigPicker
          show={Boolean(configTarget)}
          item={configTarget}
          planStep={configPlanStep}
          locationId={sessionDetail?.location_id}
          authUser={getUser()}
          onCancel={handleConfigCancel}
          onConfirm={handleConfigConfirm}
        />
      )}
      <button
        className={`${styles.launcher} ${isOpen ? styles.launcherOpen : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? 'Close Berry chat' : 'Chat with Berry'}
        aria-expanded={isOpen}
      >
        {isOpen ? '✕' : '🍓'}
      </button>
    </>
  );
};

export default BerryChatWidget;
